import React, { useEffect, useRef, useState } from 'react';
import { Activity, Clock, Pencil, SlidersHorizontal, Target, Tag as TagIcon, TrendingDown, TrendingUp, Wallet } from 'lucide-react';
import type { Position } from '../services/api';
import { formatCurrency } from '../utils/currency';
import { DataTable, type Column } from './DataTable';
import ErrorDisplay from './ErrorDisplay';
import PositionChartModal from './PositionChartModal';
import {
  formatPlainNumber,
  getPositionDisplaySymbol,
  getPositionStrategies,
  getPositionRowKey,
  getPositionDisplayStrategies,
  getStrategyTagColor,
} from './positionsOrdersUtils';

const POSITION_COLUMN_STORAGE_KEY = 'dashboardPositionsHiddenColumns_v1';
const DEFAULT_HIDDEN_POSITION_COLUMNS = ['margin_used'];
const POSITION_COLUMN_OPTIONS = [
  { id: 'secType', label: 'Type' },
  { id: 'strategy', label: 'Strategy' },
  { id: 'strategy_tags', label: 'Labels' },
  { id: 'quantity', label: 'Quantity' },
  { id: 'avg_cost', label: 'Entry Price' },
  { id: 'current_price', label: 'Market Price' },
  { id: 'market_value', label: 'Market Value' },
  { id: 'margin_used', label: 'Margin' },
  { id: 'daily_pnl', label: 'Daily P&L' },
  { id: 'daily_pnl_pct', label: 'Daily %' },
  { id: 'unrealized_pnl', label: 'Unrealized P&L' },
] as const;

const getInitialHiddenPositionColumns = (): Set<string> => {
  if (typeof window === 'undefined') return new Set(DEFAULT_HIDDEN_POSITION_COLUMNS);

  try {
    const stored = window.localStorage.getItem(POSITION_COLUMN_STORAGE_KEY);
    if (stored === null) return new Set(DEFAULT_HIDDEN_POSITION_COLUMNS);

    const parsed: unknown = JSON.parse(stored);
    if (!Array.isArray(parsed)) return new Set(DEFAULT_HIDDEN_POSITION_COLUMNS);
    return new Set(parsed.filter((id): id is string =>
      typeof id === 'string' && POSITION_COLUMN_OPTIONS.some(({ id: validId }) => validId === id)
    ));
  } catch {
    return new Set(DEFAULT_HIDDEN_POSITION_COLUMNS);
  }
};

const getDailyAssetChangePercent = (position: Position): number | null => {
  if (position.daily_pnl === undefined || position.daily_pnl === null) return null;
  const dailyPnl = Number(position.daily_pnl);
  if (!Number.isFinite(dailyPnl)) return null;

  const brokerMarketValue = position.market_value === undefined || position.market_value === null
    ? Number.NaN
    : Number(position.market_value);
  const parsedMultiplier = Number(position.multiplier);
  const multiplier = Number.isFinite(parsedMultiplier) && parsedMultiplier > 0 ? parsedMultiplier : 1;
  const currentMarketValue = Number.isFinite(brokerMarketValue)
    ? brokerMarketValue
    : Number(position.current_price) * Number(position.quantity) * multiplier;
  const previousMarketValue = currentMarketValue - dailyPnl;
  if (!Number.isFinite(previousMarketValue) || Math.abs(previousMarketValue) < 0.000001) return null;

  return (dailyPnl / previousMarketValue) * 100;
};

interface OpenPositionsTableProps {
  positions: Position[];
  hasAnyPositions: boolean;
  positionsSucceeded: boolean;
  loading: boolean;
  refreshing: boolean;
  error: string | null;
  lastUpdated: string | null;
  accountCurrency: string;
  stale: boolean;
  snapshotAvailable: boolean;
  initializing: boolean;
  connectionUnavailable: boolean;
  onRetry: () => void;
  onClosePosition: (position: Position) => void;
  onOpenPositionChart: (position: Position) => void;
  onEditPositionTags: (position: Position) => void;
}

const OpenPositionsTable: React.FC<OpenPositionsTableProps> = ({
  positions,
  hasAnyPositions,
  positionsSucceeded,
  loading,
  refreshing,
  error,
  lastUpdated,
  accountCurrency,
  stale,
  snapshotAvailable,
  initializing,
  connectionUnavailable,
  onRetry,
  onClosePosition,
  onOpenPositionChart,
  onEditPositionTags,
}) => {
  const [expandedChartPositionKey, setExpandedChartPositionKey] = useState<string | null>(null);
  const [hiddenColumnIds, setHiddenColumnIds] = useState(getInitialHiddenPositionColumns);
  const [columnMenuOpen, setColumnMenuOpen] = useState(false);
  const columnMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(POSITION_COLUMN_STORAGE_KEY, JSON.stringify([...hiddenColumnIds]));
    } catch {
      // Keep column selection usable when browser storage is unavailable.
    }
  }, [hiddenColumnIds]);

  useEffect(() => {
    if (!columnMenuOpen) return undefined;

    const closeOnOutsideClick = (event: MouseEvent) => {
      if (!columnMenuRef.current?.contains(event.target as Node)) setColumnMenuOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setColumnMenuOpen(false);
    };
    document.addEventListener('mousedown', closeOnOutsideClick);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeOnOutsideClick);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [columnMenuOpen]);

  const toggleColumn = (columnId: string) => {
    setHiddenColumnIds((current) => {
      const next = new Set(current);
      if (next.has(columnId)) next.delete(columnId);
      else next.add(columnId);
      return next;
    });
  };

  const columns: Column<Position>[] = [
    {
      id: 'symbol',
      header: 'Symbol',
      accessor: 'symbol',
      sortable: true,
      className: 'w-[1%]',
      headerClassName: 'w-[1%]',
      render: (position) => (
        <div className="flex items-center">
          <span className="font-semibold text-[12px] tracking-tight text-white">{getPositionDisplaySymbol(position)}</span>
        </div>
      ),
    },
    {
      id: 'secType',
      header: 'Type',
      accessor: (position) => position.secType || 'STK',
      sortable: true,
      className: 'text-white/70 text-[12px]',
    },
    {
      id: 'strategy',
      header: 'Strategy',
      accessor: (position) => getPositionDisplayStrategies(position).join(', '),
      sortable: true,
      render: (position) => (
        <div className="flex items-center gap-1">
          <div className="flex flex-wrap gap-1">
            {getPositionDisplayStrategies(position).map((strategy, index) => (
              <span
                key={strategy}
                title={(position.strategy_order_lots || [])
                  .filter((lot) => lot.strategy === (getPositionStrategies(position)[index] || strategy))
                  .map((lot) => `${lot.strategy}: opened ${lot.opened_at || 'date unavailable'}${lot.opening_order_id ? ` by order #${lot.opening_order_id}` : ''}`)
                  .join('\n') || undefined}
                className={`rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${strategy === 'UNASSIGNED'
                  ? 'border border-white/10 bg-white/5 text-white/45'
                  : 'border border-neon/20 bg-neon/10 text-neon'
                  }`}
              >
                {strategy}
              </span>
            ))}
          </div>
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onEditPositionTags(position);
            }}
            className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-md border border-white/10 text-white/40 transition-colors hover:border-neon/30 hover:bg-neon/10 hover:text-neon"
            title="Edit position strategy label"
            aria-label={`Edit position strategy label for ${getPositionDisplaySymbol(position)}`}
          >
            <Pencil className="h-3 w-3" />
          </button>
        </div>
      ),
    },
    {
      id: 'strategy_tags',
      header: 'Labels',
      headerInfo: 'Descriptive labels attached to this position for notes and reminders.',
      accessor: (position) => (position.strategy_tags || []).map((tag) => tag.name).join(', '),
      sortable: true,
      className: 'w-[1%]',
      headerClassName: 'w-[1%]',
      render: (position) => (
        <div className="flex max-w-[200px] flex-wrap items-center gap-1">
          {(position.strategy_tags || []).map((tag) => (
            <span
              key={tag.id}
              title={tag.description || tag.name}
              className="max-w-[76px] truncate rounded-sm border px-1 py-0.5 text-[9px] font-bold tracking-wide text-white/85"
              style={{ borderColor: `${getStrategyTagColor(tag)}66`, backgroundColor: `${getStrategyTagColor(tag)}1A` }}
            >
              {tag.name}
            </span>
          ))}
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onEditPositionTags(position);
            }}
            className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-sm border border-dashed border-white/20 text-white/50 transition-colors hover:border-neon/40 hover:text-neon"
            title={position.strategy_tags?.length ? 'Edit position labels' : 'Add position labels'}
            aria-label={position.strategy_tags?.length ? 'Edit position labels' : 'Add position labels'}
          >
            <TagIcon className="h-3 w-3" />
          </button>
        </div>
      ),
    },
    {
      id: 'quantity',
      header: 'Quantity',
      accessor: 'quantity',
      sortable: true,
      render: (position) => (
        <div className={`flex items-center gap-1.5 ${position.quantity && position.quantity < 0 ? 'text-rose-400' : 'text-white'}`}>
          <span className="font-mono text-[14px] font-bold">{position.quantity}</span>
        </div>
      ),
    },
    {
      id: 'avg_cost',
      header: 'Entry Price',
      accessor: 'avg_cost',
      sortable: true,
      render: (position) => (
        <span className="text-white/90 font-mono text-[13px]">
          {position.avg_cost !== undefined && position.avg_cost !== null ? formatCurrency(position.avg_cost, position.currency) : '-'}
        </span>
      ),
    },
    {
      id: 'current_price',
      header: 'Mkt Price',
      accessor: 'current_price',
      sortable: true,
      render: (position) => (
        <span className={`font-mono text-[13px] font-bold ${refreshing ? 'animate-pulse text-neon' : 'text-white/90'}`}>
          {position.current_price !== undefined && position.current_price !== null ? formatCurrency(position.current_price, position.currency) : '-'}
          {position.quote_stale && <span className="ml-1 text-[9px] text-amber-300">Stale</span>}
        </span>
      ),
    },
    {
      id: 'market_value',
      header: 'Market Value',
      accessor: 'market_value',
      sortable: true,
      render: (position) => (
        <span className={`font-mono text-[13px] font-bold ${refreshing ? 'animate-pulse text-neon' : 'text-white/80'}`}>
          {formatCurrency(position.market_value, position.currency)}
        </span>
      ),
    },
    {
      id: 'margin_used',
      header: 'Margin',
      headerInfo: 'Estimated margin allocated to this position. The percentage is relative to total account value.',
      accessor: 'margin_used',
      sortable: true,
      render: (position) => (
        <div className="font-mono text-[13px] font-bold text-white/80 leading-tight">
          {position.margin_used_pct !== undefined && position.margin_used_pct !== null && (
            <div>{position.margin_used_pct.toFixed(2)}%</div>
          )}
          <div className="text-[11px] font-normal text-muted">{formatPlainNumber(position.margin_used)}</div>
        </div>
      ),
    },
    {
      id: 'daily_pnl',
      header: 'Daily P&L',
      accessor: 'daily_pnl',
      sortable: true,
      render: (position) => (
        <div className={`font-black font-mono text-[14px] ${(position.daily_pnl || 0) < 0 ? 'text-rose-400' : (position.daily_pnl || 0) > 0 ? 'text-emerald-400' : 'text-white/40'}`}>
          {position.daily_pnl !== undefined && position.daily_pnl !== null ? formatCurrency(position.daily_pnl, position.currency) : '-'}
        </div>
      ),
    },
    {
      id: 'daily_pnl_pct',
      header: 'Daily %',
      headerInfo: 'Estimated underlying daily price change, calculated from daily P&L and the prior position value.',
      accessor: (position) => getDailyAssetChangePercent(position),
      sortable: true,
      render: (position) => {
        const dailyPercent = getDailyAssetChangePercent(position);
        return (
          <div className={`font-black font-mono text-[13px] ${dailyPercent === null ? 'text-white/40' : dailyPercent > 0 ? 'text-emerald-400' : dailyPercent < 0 ? 'text-rose-400' : 'text-white/80'}`}>
            {dailyPercent === null ? '—' : `${dailyPercent > 0 ? '+' : ''}${dailyPercent.toFixed(2)}%`}
          </div>
        );
      },
    },
    {
      id: 'unrealized_pnl',
      header: 'Unrealized P&L',
      accessor: 'unrealized_pnl',
      sortable: true,
      render: (position) => (
        <div className={`font-black font-mono text-[14px] ${(position.unrealized_pnl || 0) < 0 ? 'text-rose-400' : (position.unrealized_pnl || 0) > 0 ? 'text-emerald-400' : 'text-white/40'}`}>
          {position.unrealized_pnl !== undefined && position.unrealized_pnl !== null ? formatCurrency(position.unrealized_pnl, position.currency) : '-'}
        </div>
      ),
    },
    {
      id: 'actions',
      header: 'Actions',
      accessor: 'id' as any,
      align: 'right',
      render: (position) => (
        <button
          className="px-3 py-1.5 rounded-lg border border-rose-500/30 bg-rose-500/10 text-[10px] font-bold uppercase tracking-[0.12em] text-rose-200 hover:bg-rose-500/20 active:scale-95 transition-all disabled:opacity-50"
          disabled={refreshing}
          onClick={(event) => {
            event.stopPropagation();
            onClosePosition(position);
          }}
        >
          Close
        </button>
      ),
    },
  ];
  const visibleColumns = columns.filter((column) =>
    column.id === 'symbol' || column.id === 'actions' || !hiddenColumnIds.has(column.id)
  );

  const totalPositionValue = positions.reduce((sum, position) => {
    const marketValue = position.market_value !== undefined && position.market_value !== null
      ? Number(position.market_value)
      : Number.NaN;
    if (Number.isFinite(marketValue)) return sum + Math.abs(marketValue);

    const price = Number(position.current_price);
    const quantity = Number(position.quantity);
    const parsedMultiplier = Number(position.multiplier);
    const multiplier = Number.isFinite(parsedMultiplier) && parsedMultiplier > 0 ? parsedMultiplier : 1;
    return Number.isFinite(price) && Number.isFinite(quantity)
      ? sum + Math.abs(price * quantity * multiplier)
      : sum;
  }, 0);
  const totalDailyPnl = positions.reduce((sum, position) => sum + (position.daily_pnl || 0), 0);
  const isDailyPositive = totalDailyPnl >= 0;
  const totalUnrealized = positions.reduce((sum, position) => sum + (position.unrealized_pnl || 0), 0);
  const isPositive = totalUnrealized >= 0;

  return (
    <div className="flex min-h-0 flex-1 flex-col space-y-6">
      {stale && (
        <div className="rounded-xl border border-warning-500/25 bg-warning-500/10 px-4 py-3 text-xs text-warning-300">
          {initializing
            ? 'IB is connected and positions are synchronizing. This panel will refresh automatically.'
            : connectionUnavailable
              ? 'IB is connected, but live positions are temporarily unavailable — showing the latest persisted snapshot.'
            : !snapshotAvailable
              ? 'Gateway offline — no persisted position snapshot is available yet.'
              : 'Gateway offline — showing the last persisted position snapshot.'}
        </div>
      )}
      {!error && positionsSucceeded && (
        <div className="flex flex-wrap items-center gap-3 pb-2">
          {hasAnyPositions && (
            <>
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-[10px] font-bold uppercase tracking-wider text-emerald-400">
                <Target size={12} />
                {positions.length} Active {positions.length === 1 ? 'Position' : 'Positions'}
              </div>
              <div
                className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-[10px] font-bold uppercase tracking-wider text-white/80"
                title="Gross market value of the positions matching the current filters"
              >
                <Wallet size={12} />
                Total value: {formatCurrency(totalPositionValue, accountCurrency)}
              </div>
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-[10px] font-bold uppercase tracking-wider ${isDailyPositive ? 'text-emerald-400' : 'text-rose-400'}`}>
                <Activity size={12} />
                Daily P&amp;L: {formatCurrency(totalDailyPnl, accountCurrency)}
              </div>
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-[10px] font-bold uppercase tracking-wider ${isPositive ? 'text-emerald-400' : 'text-rose-400'}`}>
                {isPositive ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                Unrealized: {formatCurrency(totalUnrealized, accountCurrency)}
              </div>
              {lastUpdated && (
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-[10px] font-bold uppercase tracking-wider text-muted">
                  <Clock size={12} />
                  Refreshed: {lastUpdated}
                </div>
              )}
            </>
          )}
          <div className="relative ml-auto" ref={columnMenuRef}>
            <button
              type="button"
              onClick={() => setColumnMenuOpen((open) => !open)}
              aria-label="Choose visible position fields"
              aria-expanded={columnMenuOpen}
              className="inline-flex items-center gap-2 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-white/70 transition-colors hover:border-neon/30 hover:bg-neon/10 hover:text-neon"
            >
              <SlidersHorizontal className="h-3.5 w-3.5" />
              Fields
            </button>
            {columnMenuOpen && (
              <div className="absolute right-0 top-full z-30 mt-2 max-h-[70vh] w-56 overflow-y-auto rounded-xl border border-white/10 bg-slate-950 p-3 shadow-2xl">
                <div className="mb-2 px-1 text-[9px] font-bold uppercase tracking-widest text-muted">Shown fields</div>
                <div className="space-y-1">
                  {POSITION_COLUMN_OPTIONS.map(({ id, label }) => (
                    <label key={id} className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-xs text-white/75 transition-colors hover:bg-white/5 hover:text-white">
                      <input
                        type="checkbox"
                        checked={!hiddenColumnIds.has(id)}
                        onChange={() => toggleColumn(id)}
                        className="h-3.5 w-3.5 accent-teal-400"
                      />
                      {label}
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      {error && (
        <ErrorDisplay error={error} onRetry={onRetry} message="Failed to load positions" />
      )}

      <DataTable
        data={positions}
        columns={visibleColumns}
        className="min-h-0 flex-1"
        compact
        loading={loading}
        onRowClick={(position) => {
          const rowKey = getPositionRowKey(position);
          setExpandedChartPositionKey((current) => current === rowKey ? null : rowKey);
        }}
        getRowKey={getPositionRowKey}
        expandedRowKey={expandedChartPositionKey}
        renderExpandedRow={(position) => (
          <PositionChartModal
            position={position}
            inline
            onClose={() => setExpandedChartPositionKey(null)}
            onOpenModal={() => onOpenPositionChart(position)}
          />
        )}
        emptyMessage={stale && !snapshotAvailable
          ? initializing ? 'Synchronizing positions…' : 'No persisted position snapshot available'
          : 'No open positions found'}
        initialSort={{ id: 'unrealized_pnl', direction: 'desc' }}
      />
    </div>
  );
};

export default OpenPositionsTable;
